import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';

import { getFallEvents, updateFallEvent } from '../../services/fallEventService';
import { useAuth } from '../../contexts/AuthContext';
import { scopeEventsForClient } from '../../utils/eventFilters';
import { buildClipUrl } from '../../utils/media';
import FallEventDrawer from '../admin/components/fall-events/FallEventDrawer';

const renderConfidence = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${Math.max(0, Math.min(100, value * 100)).toFixed(1)}%`;
  }
  return 'N/A';
};

const FallLogsPage = () => {
  const { user } = useAuth();
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getFallEvents();
      const normalised = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
      setRawEvents(normalised);
    } catch (err) {
      setError('Unable to load fall history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const scopedEvents = useMemo(() => scopeEventsForClient(rawEvents, user), [rawEvents, user]);
  const displayedEvents = useMemo(
    () => (scopedEvents.events.length ? scopedEvents.events : rawEvents),
    [rawEvents, scopedEvents.events],
  );

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setDrawerOpen(true);
  };

  const handleSaveEvent = async (eventId, payload) => {
    try {
      const updatedEvent = await updateFallEvent(eventId, payload);
      setRawEvents((prev) => prev.map((event) => (event.id === eventId ? { ...event, ...updatedEvent } : event)));
      setSelectedEvent(updatedEvent);
      setDrawerOpen(false);
      setError('');
    } catch (err) {
      setError('Unable to update this event right now. Please try again.');
    }
  };

  return (
    <Container>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        mb={3}
        sx={{ flexWrap: 'wrap', gap: 2 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            My Fall History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the incidents detected for your profile. Select a row to inspect the clip, notes, or status updates.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadEvents} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {scopedEvents.fallback && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We could not find incidents tagged directly to your profile. Showing the broad fall log so you do not miss any
          urgent updates.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : displayedEvents.length === 0 ? (
        <Box mt={4}>
          <Typography variant="body1" color="text.secondary" align="center">
            There are no recorded falls for your profile yet. You&apos;ll see new incidents here as soon as they are
            captured.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Fall Confidence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    {event.timestamp ? new Date(event.timestamp).toLocaleString() : 'Not specified'}
                  </TableCell>
                  <TableCell>{event.camera?.name || event.camera_name || 'Unknown camera'}</TableCell>
                  <TableCell>{renderConfidence(event.confidence)}</TableCell>
                  <TableCell>{event.status || 'Pending'}</TableCell>
                  <TableCell>{event.label || 'Pending'}</TableCell>
                  <TableCell>{event.client_notes || event.notes || '-'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => handleViewEvent(event)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <FallEventDrawer
        open={drawerOpen}
        event={selectedEvent}
        buildClipUrl={buildClipUrl}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveEvent}
      />
    </Container>
  );
};

export default FallLogsPage;
